import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/leaveApplication.css";
import apiClient from "../api/api.js";
import Calendar from "./Calendar";

const LEAVE_TYPES = ["Sick", "Annual", "Authorised", "Maternity", "Paternity", "Bereavement"];

function generateDateRange(start, end) {
  const dates = [];
  let current = new Date(start);
  const endDate = new Date(end);
  while (current <= endDate) {
    dates.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Get the current user ID (implement this based on your auth system)
function getCurrentUserId() {
  return sessionStorage.getItem('user_id');
}

// Updated function to only check conflicts for the current user
function getExistingLeaveDatesForCurrentUser(approvedLeaves, pendingLeaves, currentUserId) {
  const existingDates = new Set();
  
  // If no current user ID, we can't filter properly
  if (!currentUserId) {
    console.warn("No current user ID available. Using all leaves (might cause false conflicts).");
    // Fallback to checking all leaves if no user ID available
    return getExistingLeaveDatesFromAPI(approvedLeaves, pendingLeaves);
  }
  
  // console.log("Filtering leaves for user ID:", currentUserId);
  
  // Filter and process approved leaves for current user
  const userApprovedLeaves = approvedLeaves.filter(leave => {
    // Check various possible property names for user identification
    const userId = leave.user_id || leave.employee_id || leave.userId || leave.employeeId;
    return userId === currentUserId || userId === parseInt(currentUserId);
  });
  
  // Filter and process pending leaves for current user
  const userPendingLeaves = pendingLeaves.filter(leave => {
    // Check various possible property names for user identification
    const userId = leave.user_id || leave.employee_id || leave.userId || leave.employeeId;
    return userId === currentUserId || userId === parseInt(currentUserId);
  });
  
  // console.log("User's approved leaves:", userApprovedLeaves);
  // console.log("User's pending leaves:", userPendingLeaves);
  
  // Process dates for current user's leaves only
  [...userApprovedLeaves, ...userPendingLeaves].forEach(leave => {
    const startDate = leave.start_date || leave.fromDate || leave.from_date;
    const endDate = leave.end_date || leave.toDate || leave.to_date;
    
    if (startDate && endDate) {
      const leaveDates = generateDateRange(startDate, endDate);
      leaveDates.forEach(date => existingDates.add(date));
    }
  });
  
  return existingDates;
}

// Alternative approach if you don't have user IDs in the leave data
// This assumes the API endpoints already return only current user's data
function getExistingLeaveDatesFromAPI(approvedLeaves, pendingLeaves) {
  const existingDates = new Set();
  
  // Process approved leaves (should already be filtered by API)
  approvedLeaves.forEach(leave => {
    const startDate = leave.start_date || leave.fromDate || leave.from_date;
    const endDate = leave.end_date || leave.toDate || leave.to_date;
    
    if (startDate && endDate) {
      const leaveDates = generateDateRange(startDate, endDate);
      leaveDates.forEach(date => existingDates.add(date));
    }
  });
  
  // Process pending leaves (should already be filtered by API)
  pendingLeaves.forEach(leave => {
    const startDate = leave.start_date || leave.fromDate || leave.from_date;
    const endDate = leave.end_date || leave.toDate || leave.to_date;
    
    if (startDate && endDate) {
      const leaveDates = generateDateRange(startDate, endDate);
      leaveDates.forEach(date => existingDates.add(date));
    }
  });
  
  return existingDates;
}

// Helper function to check if there's any date conflict
function hasDateConflict(fromDate, toDate, existingLeaveDates) {
  const requestedDates = generateDateRange(fromDate, toDate);
  return requestedDates.some(date => existingLeaveDates.has(date));
}

// Helper function to get conflicting dates
function getConflictingDates(fromDate, toDate, existingLeaveDates) {
  const requestedDates = generateDateRange(fromDate, toDate);
  return requestedDates.filter(date => existingLeaveDates.has(date));
}

const transformLeaveData = (data) => {
  if (!Array.isArray(data)) {
    // console.error("API response is not an array:", data);
    return [];
  }

  try {
    return data.map((leave) => {
      if (!leave.id || !leave.start_date || !leave.end_date || !leave.type) {
        console.error("Missing required fields in leave item:", leave);
        return null;
      }

      return {
        id: leave.id,
        fromDate: leave.start_date,
        toDate: leave.end_date,
        leaveType: leave.type.charAt(0).toUpperCase() + leave.type.slice(1) + " Leave",
        reason: leave.reason || "No reason provided",
        status: leave.status || "pending",
      };
    }).filter((item) => item !== null);
  } catch (error) {
    console.error("Error in transformLeaveData:", error);
    return [];
  }
};

export default function LeaveApplication() {
  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    leaveType: "",
    reason: "",
  });
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateConflictError, setDateConflictError] = useState("");
  const requestIdRef = useRef(0);

  useEffect(() => {
    const fetchLeaveData = async () => {
      const currentRequestId = ++requestIdRef.current;
      try {
        // Fetch both approved and pending leaves
        const [approvedRes, pendingRes] = await Promise.all([
          apiClient.get("/leave/approved"),
          apiClient.get("/leave")
        ]);

        if (currentRequestId !== requestIdRef.current) return;

        // Set approved leaves
        if (Array.isArray(approvedRes.data)) {
          setApprovedLeaves(approvedRes.data);
        } else {
          setApprovedLeaves([]);
        }

        // Set pending leaves
        if (Array.isArray(pendingRes.data)) {
          setPendingLeaves(pendingRes.data);
          // Transform pending leaves for display in history table
          const transformedPending = transformLeaveData(pendingRes.data);
          setLeaveHistory(transformedPending);
        } else {
          setPendingLeaves([]);
          setLeaveHistory([]);
        }

      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("Error fetching leave data:", err);
        setError("Failed to fetch leave data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeaveData();
  }, []);

  // Check for date conflicts whenever dates change
  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      // Add debug logging
      // debugLeaveData(approvedLeaves, pendingLeaves);
      
      const currentUserId = getCurrentUserId();
      let existingLeaveDates;
      
      if (currentUserId) {
        // Use this if you have user IDs in your leave data
        existingLeaveDates = getExistingLeaveDatesForCurrentUser(
          approvedLeaves, 
          pendingLeaves, 
          currentUserId
        );
      } else {
        // Use this if your API already filters by current user
        existingLeaveDates = getExistingLeaveDatesFromAPI(approvedLeaves, pendingLeaves);
      }
      
      // console.log("Checking dates for conflicts:", formData.fromDate, "to", formData.toDate);
      // console.log("Against existing dates:", Array.from(existingLeaveDates));
      
      if (hasDateConflict(formData.fromDate, formData.toDate, existingLeaveDates)) {
        const conflictingDates = getConflictingDates(formData.fromDate, formData.toDate, existingLeaveDates);
        setDateConflictError(
          `You already have leave requests for the following date(s): ${conflictingDates.join(", ")}. Please choose different dates.`
        );
      } else {
        setDateConflictError("");
      }
    } else {
      setDateConflictError("");
    }
  }, [formData.fromDate, formData.toDate, approvedLeaves, pendingLeaves]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { fromDate, toDate, leaveType, reason } = formData;

    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From date cannot be after To date");
      return;
    }

    // Check for date conflicts before submitting with proper user filtering
    const currentUserId = getCurrentUserId();
    let existingLeaveDates;
    
    if (currentUserId) {
      existingLeaveDates = getExistingLeaveDatesForCurrentUser(
        approvedLeaves, 
        pendingLeaves, 
        currentUserId
      );
    } else {
      existingLeaveDates = getExistingLeaveDatesFromAPI(approvedLeaves, pendingLeaves);
    }
    
    if (hasDateConflict(fromDate, toDate, existingLeaveDates)) {
      toast.error("Cannot apply for leave on dates that already have approved or pending leave requests");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        start_date: fromDate,
        end_date: toDate,
        reason,
        type: leaveType.toLowerCase(),
      };

      const response = await apiClient.post("/leave/request", payload);
      toast.success("Leave application submitted successfully!");

      // Refresh both approved and pending leaves after submission
      const [approvedRes, pendingRes] = await Promise.all([
        apiClient.get("/leave/approved"),
        apiClient.get("/leave")
      ]);

      if (Array.isArray(approvedRes.data)) {
        setApprovedLeaves(approvedRes.data);
      }

      if (Array.isArray(pendingRes.data)) {
        setPendingLeaves(pendingRes.data);
        const transformedData = transformLeaveData(pendingRes.data);
        setLeaveHistory(transformedData);
      }

      setFormData({ fromDate: "", toDate: "", leaveType: "", reason: "" });
      setShowModal(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Unknown error occurred";
      toast.error(`Error submitting leave application: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalLeaveDays = formData.fromDate && formData.toDate
    ? generateDateRange(formData.fromDate, formData.toDate).length
    : 0;

  const resetForm = () => {
    setFormData({ fromDate: "", toDate: "", leaveType: "", reason: "" });
    setDateConflictError("");
    setShowModal(false);
  };

  if (isLoading) {
    return (
      <div className="main-container">
        <div className="leave-container">
          <h2 className="leave-title">Leave Management</h2>
          <p>Loading leave history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="leave-container">
        <ToastContainer />
        
        {/* Header with left and right buttons */}
        <div className="header-container">
          <div className="header-left">
            <button
              className="records-btn"
              onClick={() => setShowCalendarPopup(true)}
            >
              Records
            </button>
          </div>
          
          <h2 className="leave-title">Leave Management</h2>
          
          <div className="header-right">
            <button
              className="apply-leave-btn"
              onClick={() => setShowModal(true)}
            >
              Apply for Leave
            </button>
          </div>
        </div>

        <h3 className="leave-subtitle">My Leave History</h3>

        {error && (
          <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="leave-table-wrapper">
          <table className="leave-table">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Type</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {leaveHistory.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "2rem" }}>
                    {isLoading ? "Loading..." : error ? "Error loading data" : "No leave history found"}
                  </td>
                </tr>
              ) : (
                leaveHistory.map((leave) => (
                  <tr key={leave.id}>
                    <td>{formatDate(leave.fromDate)}</td>
                    <td>{formatDate(leave.toDate)}</td>
                    <td>{leave.leaveType}</td>
                    <td>{leave.reason}</td>
                    <td className={`status-${leave.status || "pending"}`}>
                      {leave.status === "approved"
                        ? "✓ Approved"
                        : leave.status === "rejected"
                        ? "✗ Rejected"
                        : "⏳ Pending"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Apply for Leave</h3>
                <button className="modal-close-btn" onClick={resetForm}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="leave-form">
                <div>
                  <label htmlFor="fromDate">From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="toDate">To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={formData.toDate}
                    onChange={handleChange}
                    min={formData.fromDate || new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>

                {/* Date conflict error message */}
                {dateConflictError && (
                  <div className="error-message" style={{ 
                    color: 'red', 
                    marginBottom: '1rem',
                    padding: '0.5rem',
                    backgroundColor: '#ffebee',
                    border: '1px solid #ffcdd2',
                    borderRadius: '4px'
                  }}>
                    {dateConflictError}
                  </div>
                )}

                <div>
                  <label htmlFor="leaveType">Leave Type</label>
                  <select
                    name="leaveType"
                    value={formData.leaveType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Leave Type --</option>
                    {LEAVE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type} Leave
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="reason">Reason</label>
                  <textarea
                    name="reason"
                    rows="4"
                    value={formData.reason}
                    onChange={handleChange}
                    required
                  />
                </div>

                {formData.fromDate && formData.toDate && (
                  <p className="leave-summary">
                    You are applying for <strong>{totalLeaveDays}</strong> day(s) of leave.
                  </p>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isSubmitting || dateConflictError}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCalendarPopup && (
          <div className="modal-overlay">
            <div className="modal-content calendar-modal" style={{ maxWidth: '900px' }}>
              <div className="modal-header">
                <h3>Leave Records Calendar</h3>
                <button 
                  className="modal-close-btn" 
                  onClick={() => setShowCalendarPopup(false)}
                >
                  ×
                </button>
              </div>
              <div className="calendar-popup-content">
                <Calendar />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}